import { useMutation } from "@apollo/client";
import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { TENANTS_QUERY, TenantOption } from "components/MultipleTenantPicker";
import { getNotificationProgramAreaLabel } from "components/Notifications/notificationPresentation";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  MultipleTenantPickerQuery,
  NotificationPriority,
  ProgramArea,
} from "generated-graphql/graphql";
import { GET_NOTIFICATIONS, QUEUE_STAFF_NOTIFICATION } from "hooks/useNotifications";
import { useState } from "react";
import { getUserFacingMessage } from "utils/errorUtils";

const notificationPriorities = Object.values(NotificationPriority);
const notificationProgramAreas = Object.values(ProgramArea);

function isNotificationPriority(value: string): value is NotificationPriority {
  return notificationPriorities.some((priority) => priority === value);
}

function isProgramArea(value: string): value is ProgramArea {
  return notificationProgramAreas.some((programArea) => programArea === value);
}

/**
 * A notification is about at most one record, so the form offers a single
 * subject rather than one field per entity type. The mutation still takes a
 * per-entity id, so the choice is mapped back to the matching input field.
 */
type SubjectTypeOption = "" | "TASK" | "TIER_II_REPORT" | "SUBMISSION" | "JOB";

const subjectTypeOptions: { value: Exclude<SubjectTypeOption, "">; label: string }[] = [
  { value: "TASK", label: "Task" },
  { value: "TIER_II_REPORT", label: "Tier II report" },
  { value: "SUBMISSION", label: "Submission" },
  { value: "JOB", label: "Background job" },
];

function isSubjectTypeOption(value: string): value is SubjectTypeOption {
  return value === "" || subjectTypeOptions.some((option) => option.value === value);
}

type SubjectSelection = {
  taskId?: string;
  tierIIReportId?: string;
  submissionId?: string;
  jobId?: string;
};

function buildSubjectSelection(
  subjectType: SubjectTypeOption,
  subjectId: string
): SubjectSelection {
  const trimmedId = subjectId.trim();
  if (trimmedId.length === 0) {
    return {};
  }
  switch (subjectType) {
    case "TASK":
      return { taskId: trimmedId };
    case "TIER_II_REPORT":
      return { tierIIReportId: trimmedId };
    case "SUBMISSION":
      return { submissionId: trimmedId };
    case "JOB":
      return { jobId: trimmedId };
    default:
      return {};
  }
}

function getTenantItems(data: MultipleTenantPickerQuery): TenantOption[] {
  return data.tenants?.items ?? [];
}

function getTenantLabel(tenant: TenantOption): string {
  return tenant.name;
}

function parseUserIds(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((userId) => userId.trim())
    .filter((userId) => userId.length > 0);
}

export function QueueNotificationDialog({ onClose }: { onClose: () => void }) {
  const [organization, setOrganization] = useState<TenantOption | null>(null);
  const [userIdsText, setUserIdsText] = useState("");
  const [title, setTitle] = useState("");
  const [bodyMarkdown, setBodyMarkdown] = useState("");
  const [programArea, setProgramArea] = useState<ProgramArea | "">("");
  const [priority, setPriority] = useState<NotificationPriority>(NotificationPriority.Normal);
  const [subjectType, setSubjectType] = useState<SubjectTypeOption>("");
  const [subjectId, setSubjectId] = useState("");
  const [queueStaffNotification, queueState] = useMutation(QUEUE_STAFF_NOTIFICATION, {
    refetchQueries: [GET_NOTIFICATIONS],
  });

  const handlePriorityChange = (event: SelectChangeEvent<string>) => {
    if (isNotificationPriority(event.target.value)) {
      setPriority(event.target.value);
    }
  };

  const handleProgramAreaChange = (event: SelectChangeEvent<string>) => {
    const nextProgramArea = event.target.value;
    if (nextProgramArea === "" || isProgramArea(nextProgramArea)) {
      setProgramArea(nextProgramArea);
    }
  };

  const handleSubjectTypeChange = (event: SelectChangeEvent<string>) => {
    if (isSubjectTypeOption(event.target.value)) {
      setSubjectType(event.target.value);
    }
  };

  const handleQueue = async () => {
    const result = await queueStaffNotification({
      variables: {
        input: {
          userIds: parseUserIds(userIdsText),
          tenantId: organization?.id ?? null,
          title,
          bodyMarkdown,
          programArea: programArea || null,
          priority,
          ...buildSubjectSelection(subjectType, subjectId),
        },
      },
    }).catch(() => null);
    if (result?.data?.queueStaffNotification) {
      onClose();
    }
  };

  // A chosen subject type without an id would queue a notification that links
  // nowhere, so the two travel together.
  const hasCompleteSubject = subjectType === "" || subjectId.trim().length > 0;
  const canQueue =
    parseUserIds(userIdsText).length > 0 &&
    title.trim().length > 0 &&
    bodyMarkdown.trim().length > 0 &&
    hasCompleteSubject;
  const errorMessage = queueState.error
    ? (getUserFacingMessage(queueState.error) ?? "The notification could not be queued. Try again.")
    : null;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Queue notification"
      maxWidth="sm"
      fullWidth
      primaryActionButtonProps={{
        saveText: "Queue",
        loading: queueState.loading,
        disabled: !canQueue,
        onClick: handleQueue,
      }}
    >
      <AsyncAutocomplete
        query={TENANTS_QUERY}
        initialPageSize={20}
        getItems={getTenantItems}
        getOptionLabel={getTenantLabel}
        placeholderLabel="Organization"
        value={organization}
        onChange={setOrganization}
        textFieldProps={{ size: "small" }}
        helperText="Optional. Leave empty to send outside any organization."
      />
      <TextField
        label="User IDs"
        value={userIdsText}
        onChange={(event) => setUserIdsText(event.target.value)}
        size="small"
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        label="Title"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        size="small"
        fullWidth
      />
      <TextField
        label="Body markdown"
        value={bodyMarkdown}
        onChange={(event) => setBodyMarkdown(event.target.value)}
        size="small"
        fullWidth
        multiline
        minRows={5}
      />
      <Stack direction="row" spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel id="notification-program-area-label">Program</InputLabel>
          <Select
            labelId="notification-program-area-label"
            label="Program"
            value={programArea}
            onChange={handleProgramAreaChange}
          >
            <MenuItem value="">None</MenuItem>
            {notificationProgramAreas.map((value) => (
              <MenuItem key={value} value={value}>
                {getNotificationProgramAreaLabel(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel id="notification-priority-label">Priority</InputLabel>
          <Select
            labelId="notification-priority-label"
            label="Priority"
            value={priority}
            onChange={handlePriorityChange}
          >
            {notificationPriorities.map((value) => (
              <MenuItem key={value} value={value}>
                {prettyPrintEnumValue(value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="notification-subject-type-label">Links to</InputLabel>
          <Select
            labelId="notification-subject-type-label"
            label="Links to"
            value={subjectType}
            onChange={handleSubjectTypeChange}
          >
            <MenuItem value="">Nothing</MenuItem>
            {subjectTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Record ID"
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
          size="small"
          fullWidth
          disabled={subjectType === ""}
          helperText={
            subjectType === ""
              ? "Pick what the notification is about to make it open that record."
              : "The recipient's notification shows this record's live status and opens it when clicked."
          }
        />
      </Stack>
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
    </Dialog>
  );
}
